import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Flame, CalendarRange, Sparkles } from "lucide-react";

export default function StreakWidget() {
  const [s, setS] = useState(null);
  const [onb, setOnb] = useState(null);

  useEffect(() => {
    api.get("/engagement/streaks").then((r) => setS(r.data)).catch(() => setS(null));
    api.get("/engagement/onboarding").then((r) => setOnb(r.data)).catch(() => setOnb(null));
    // best-effort monthly summary email auto-trigger
    api.post("/engagement/summary/auto-check").catch(() => {});
  }, []);

  if (!s) return null;
  const onbPct = onb ? Math.round((onb.completed / onb.total) * 100) : 0;
  const showOnb = onb && !onb.dismissed && onb.completed < onb.total;

  return (
    <div className="grid sm:grid-cols-3 gap-4" data-testid="streak-widget">
      <StreakCard
        icon={Flame}
        accent="bg-orange-100 text-orange-700"
        label="Daily streak"
        value={`${s.daily} day${s.daily === 1 ? "" : "s"}`}
        sub={s.daily === 0 ? "Log something today" : "Keep it going"}
        testid="streak-daily"
      />
      <StreakCard
        icon={CalendarRange}
        accent="bg-amber-100 text-amber-800"
        label="Weekly streak"
        value={`${s.weekly} week${s.weekly === 1 ? "" : "s"}`}
        sub={s.weekly >= 4 ? "Solid rhythm" : "Building momentum"}
        testid="streak-weekly"
      />
      {showOnb ? (
        <Link to="/app/getting-started" className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 p-5 hover:border-amber-300 transition-colors" data-testid="onboarding-progress-card">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-700" />
            <span className="text-xs uppercase tracking-[0.15em] text-amber-800 font-semibold">Getting started</span>
          </div>
          <div className="font-display text-2xl font-medium mt-2">{onb.completed} / {onb.total}</div>
          <div className="text-xs text-amber-900 mt-1">{onb.total - onb.completed} step{onb.total - onb.completed === 1 ? "" : "s"} to go</div>
          <div className="mt-3 h-1.5 bg-amber-200/40 rounded-full overflow-hidden">
            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${onbPct}%` }} />
          </div>
        </Link>
      ) : (
        <StreakCard
          icon={Sparkles}
          accent="bg-emerald-100 text-emerald-700"
          label="Activities"
          value={s.total_activities}
          sub={s.last_active ? `Last: ${s.last_active}` : "No activity yet"}
          testid="streak-activities"
        />
      )}
    </div>
  );
}

function StreakCard({ icon: Icon, accent, label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={testid}>
      <div className={`w-8 h-8 rounded-full grid place-items-center ${accent}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold mt-3">{label}</div>
      <div className="font-display text-2xl font-medium mt-1">{value}</div>
      <div className="text-xs text-stone-500 mt-1">{sub}</div>
    </div>
  );
}
